<template>
  <slot
    v-if="hasOpener"
    name="modal-opener"
  >
    <button
      data-cy="modal-opener"
      :class="['btn', btnClass].join(' ')"
      @click="showModal"
    >
      <app-icon
        v-if="btnIcon"
        :name="btnIcon"
      />
      {{ btnLabel }}
    </button>
  </slot>
  <teleport to="body">
    <dialog
      ref="modalTarget"
      class="modal"
      :class="{ 'is-fixed-to-top': isFixedToTop }"
      @close="$emit(modalEvents.MODAL_HAS_CLOSED)"
    >
      <div
        v-if="isModalOpen"
        class="modal-box"
        :class="modalBoxClasses"
      >
        <slot name="modal-content">
          <modal-header
            :heading="heading"
            :subheading="subheading"
            :icon="headingIcon"
            :color="color"
            :bottomBorder="showHeaderSeparator"
            @close-modal="closeModal()"
          />
          <div class="modal-body">
            <slot name="modal-body" />
          </div>
          <modal-actions
            :cancel-action-copy="cancelActionCopy"
            @close-modal="closeModal()"
          >
            <template #secondary-action>
              <slot name="secondary-action" />
            </template>
            <template #cancel-action>
              <slot name="cancel-action" />
            </template>
            <template #primary-action>
              <slot name="primary-action" />
            </template>
          </modal-actions>
        </slot>
      </div>
    </dialog>
  </teleport>
</template>

<script setup>
import { computed, ref } from 'vue';
import { semanticColors } from '@/constants/colors';
import { modalEvents, modalSizes } from '@/constants/modals';
import ModalActions from '@/components/ui/modal/ModalActions.vue';
import ModalHeader from '@/components/ui/modal/ModalHeader.vue';

const emit = defineEmits([modalEvents.MODAL_HAS_CLOSED, modalEvents.MODAL_HAS_OPENED]);

const props = defineProps({
  btnLabel: {
    type: String,
    default: 'Open Modal',
  },
  btnClass: {
    type: String,
    default: 'btn-secondary',
  },
  btnIcon: {
    type: String,
  },
  cancelActionCopy: {
    type: String,
    default: 'Cancel',
  },
  color: {
    type: String,
    default: 'neutral',
    validator: (value) => semanticColors.includes(value),
  },
  headingIcon: {
    type: String,
  },
  heading: {
    type: String,
  },
  subheading: {
    type: String,
  },
  hasOpener: {
    type: Boolean,
    default: true,
  },
  isBodyBlock: {
    type: Boolean,
    default: false,
  },
  isFixedToTop: {
    type: Boolean,
    default: false,
  },
  modalBoxClass: {
    type: String,
  },
  showHeaderSeparator: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value),
  },
});

const modalBoxClasses = computed(() => {
  const sizeClass = modalSizes[props.size.toUpperCase()];
  const classes = [sizeClass, props.modalBoxClass];

  if (props.isBodyBlock) {
    classes.push('is-body-block');
  }

  return classes.join(' ');
});

const isModalOpen = ref(false);
const modalTarget = ref(null);

const closeModal = () => {
  modalTarget.value?.close();
  isModalOpen.value = false;
  emit(modalEvents.MODAL_HAS_CLOSED);
};

function showModal() {
  modalTarget.value?.showModal();
  isModalOpen.value = true;
  emit(modalEvents.MODAL_HAS_OPENED);
}

defineExpose({
  showModal,
  closeModal,
  isModalOpen,
});
</script>

<style>
@reference '@/assets/main.css';

.modal.is-fixed-to-top {
  @apply items-start;
  --modal-top-position: 3vh;
  @media screen and (min-height: 650px) {
    --modal-top-position: 9vh;
  }
  @media screen and (min-height: 900px) {
    --modal-top-position: 15vh;
  }
  padding-top: var(--modal-top-position);
  .modal-box {
    .modal-body {
      max-height: calc(100vh - 275px - var(--modal-top-position));
    }
  }
}

.modal-box {
  @apply px-0 overflow-y-visible;
  &.is-body-block {
    .modal-body {
      @apply py-6 border-y border-base-content/10;
    }
  }
  .modal-action,
  .modal-header,
  .modal-body {
    @apply px-6;
  }
  .modal-body {
    @apply overflow-y-auto;
    max-height: calc(100vh - 275px);
  }
}
</style>
